import { Constants } from "@eagerdog/constants";

import { IPost, IPostRunSummary } from "./post.interface";
import { IFeedAchievementItem, IFeedItem } from "./feed-item.interface";
import { runSportLabel, runSummaryText } from "./run-summary";

/**
 * Plain-text title and description for a feed item, shared by the API's
 * /preview pages (Open Graph tags), the permalink's <title>, and the share
 * modal, so a post reads the same everywhere it is quoted. Always names the
 * author (never "You"); never includes anything the post itself does not.
 */

const SITE = "Eager Dog";

/** "AKC Scent Work" etc. Alias of runSportLabel kept for existing imports. */
export const postSportLabel = (run: Pick<IPostRunSummary, "show_type" | "sanctioning_club">): string =>
  runSportLabel(run);

const trim = (text: string | undefined, max = 200): string | undefined => {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (!clean) return undefined;
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
};

const dateRange = (start?: Date | string, end?: Date | string): string | undefined => {
  if (!start) return undefined;
  const fmt = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const s = new Date(start);
  if (isNaN(s.getTime())) return undefined;
  const e = end ? new Date(end) : undefined;
  if (e && !isNaN(e.getTime()) && e.toDateString() !== s.toDateString()) return `${fmt(s)} – ${fmt(e)}`;
  return fmt(s);
};

const place = (city?: string, state?: string): string | undefined =>
  [city, state].filter(Boolean).join(", ") || undefined;

const joinParts = (parts: Array<string | undefined | null | false>, sep = " · "): string | undefined =>
  parts.filter(Boolean).join(sep) || undefined;

/** "AKC Scent Work · Excellent · Container · Qualified · Oct 10, 2026 · Judge Schedule Testing · Cambridge, AL" — same parts, same order as the feed card. */
const runLine = (run: IPostRunSummary): string | undefined => {
  const text = runSummaryText(run);
  return joinParts([text.sport, text.classLine, text.result, ...text.whereParts]);
};

/** "1 Position · 11:11:11 Search Time · 0 Faults" — the feed card's stats row as one line. */
export const runStatsLine = (run: IPostRunSummary): string | undefined =>
  joinParts(runSummaryText(run).stats.map(([label, value]) => `${value} ${label}`));

const isAchievement = (item: IFeedItem | IPost): item is IFeedAchievementItem =>
  (item as IFeedAchievementItem).type === Constants.feed_item_type.achievement;

export const postShareTitle = (item: IFeedItem | IPost): string => {
  if (isAchievement(item)) {
    return `${item.dog_call_name} earned ${item.title_name}`;
  }
  const post = item as IPost;
  const author = post.author_display_name || "Someone";
  const dog = post.dog_call_name;
  const a = post.activity;
  switch (post.post_type) {
    case Constants.post_type.club_update:
      return `${author} on ${SITE}`;
    case Constants.post_type.photo:
      return dog ? `${author} shared a photo of ${dog}` : `${author} shared a photo`;
    case Constants.post_type.video:
      return dog ? `${author} shared a video of ${dog}` : `${author} shared a video`;
    case Constants.post_type.run_result:
      return post.run_summary
        ? `${dog || author} logged a ${postSportLabel(post.run_summary)} run`
        : `${dog || author} logged a run`;
    case Constants.post_type.title_earned:
      return `${dog || "A dog"} earned ${post.title_name || "a new title"}`;
    case Constants.post_type.event_published:
      return a?.event_title ? `${author} published ${a.event_title}` : `${author} published a new event`;
    case Constants.post_type.event_registered:
      return a?.event_title ? `${author} entered ${a.event_title}` : `${author} entered an event`;
    case Constants.post_type.course_enrolled:
      return a?.course_title
        ? (dog ? `${author} enrolled ${dog} in ${a.course_title}` : `${author} enrolled in ${a.course_title}`)
        : `${author} enrolled in a course`;
    case Constants.post_type.club_followed:
    case Constants.post_type.dog_followed:
      return a?.target_name ? `${author} started following ${a.target_name}` : `${author} started following`;
    case Constants.post_type.result_entered:
      return dog ? `${dog}'s result is in` : `${author} got a result`;
    default:
      return `${author} on ${SITE}`;
  }
};

export const postShareDescription = (item: IFeedItem | IPost): string => {
  if (isAchievement(item)) {
    return joinParts([
      [item.sanctioning_club, item.show_type].filter(Boolean).join(" ") || undefined,
      item.points ? `${Math.round(item.points)} points` : undefined,
      item.dog_breed,
    ]) || `A new title on ${SITE}.`;
  }
  const post = item as IPost;
  const author = post.author_display_name || "Someone";
  const dog = post.dog_call_name;
  const a = post.activity;
  const body = trim(post.body);
  switch (post.post_type) {
    case Constants.post_type.club_update:
      return body || `An update from ${author}.`;
    case Constants.post_type.photo:
    case Constants.post_type.video:
      return body || joinParts([dog, post.dog_breed], ", ") || `Shared on ${SITE}.`;
    case Constants.post_type.run_result:
    case Constants.post_type.result_entered:
      return (post.run_summary && runLine(post.run_summary)) || body || `A run on ${SITE}.`;
    case Constants.post_type.title_earned:
      return joinParts([
        [post.sanctioning_club, post.show_type].filter(Boolean).join(" ") || undefined,
        post.points ? `${Math.round(post.points)} points` : undefined,
        post.dog_breed,
      ]) || `A new title on ${SITE}.`;
    case Constants.post_type.event_published:
      return body || joinParts([
        a?.sanctioning_club,
        dateRange(a?.start_date, a?.end_date),
        place(a?.city, a?.state),
      ]) || `A new event from ${author}.`;
    case Constants.post_type.event_registered: {
      const dogs = (a?.dogs || []).map((d) => d.call_name).filter(Boolean) as string[];
      const entered = dogs.length
        ? `${dogs.join(", ")} ${dogs.length === 1 ? "is" : "are"} entered.`
        : dog ? `${dog} is entered.` : undefined;
      return joinParts([entered, dateRange(a?.start_date, a?.end_date), place(a?.city, a?.state)], " ") || `An event entry on ${SITE}.`;
    }
    case Constants.post_type.course_enrolled:
      return joinParts([dateRange(a?.start_date, a?.end_date), a?.club_name, place(a?.city, a?.state)]) || `A course on ${SITE}.`;
    case Constants.post_type.club_followed:
      return place(a?.city, a?.state) || `A kennel club on ${SITE}.`;
    case Constants.post_type.dog_followed:
      return a?.target_breed || `A dog on ${SITE}.`;
    default:
      return body || `See what's happening in dog sports on ${SITE}.`;
  }
};

/** Generic copy for a post the public cannot see (private, held, removed, unknown). */
export const GENERIC_POST_SHARE = {
  title: `A post on ${SITE}`,
  description: `See what's happening in dog sports on ${SITE}.`,
};

/** Generic copy for a dog the public cannot see. */
export const GENERIC_DOG_SHARE = {
  title: `A dog on ${SITE}`,
  description: `Track your dog's titles and achievements on ${SITE}.`,
};

/** Site-wide fallback, also the static tags in public/index.html. */
export const SITE_SHARE = {
  title: SITE,
  description: `Enter events, track titles, and follow dogs and clubs on ${SITE}.`,
};
